/**
 * Full admin edit dialog for an Investor.
 *
 * Tabs:
 *   • Basic          — name, phone, RP code, investor type
 *   • Address        — physical + postal
 *   • Banking        — bank, branch, acct#, acct type, SWIFT/BIC
 *   • Tax & KYC      — ID / reg no, tax no, VAT, tax residency, tax ref,
 *                       tax status, contact person + KYC document manager
 *   • Account        — force set password, resend welcome, active toggle,
 *                       audit trail
 *
 * Email is intentionally NOT editable — see requirement in project brief.
 * Email CAN be changed via the "Change email" button (opens
 * ChangeEmailDialog). Backend enforces duplicate detection + forces a
 * password reset on the user.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Save, KeyRound, Upload, Trash2, FileText, ShieldCheck, Info, Mail } from 'lucide-react';
import ChangeEmailDialog from './ChangeEmailDialog';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  first_name: '', last_name: '', phone: '', risk_participant_code: '',
  investor_type: '', entity_name: '', entity_type: '', mandate_type: '',
  id_number: '', registration_number: '', tax_number: '', vat_number: '',
  physical_address: '', postal_address: '',
  bank_name: '', bank_branch_code: '', bank_account_number: '', bank_account_type: '', swift_code: '',
  tax_residency: '', tax_reference: '', tax_status: '', contact_person: '',
  annual_management_fee_pct: '', performance_sharing_rate_pct: '',
  performance_hurdle_type: 'sa_repo_rate', performance_hurdle_fixed_pct: '',
  notes: '',
};

const KYC_DOC_TYPES = [
  { key: 'id_doc', label: 'ID / Passport' },
  { key: 'proof_of_address', label: 'Proof of address' },
  { key: 'bank_letter', label: 'Bank confirmation letter' },
  { key: 'tax_clearance', label: 'Tax clearance' },
  { key: 'company_reg', label: 'Company registration (CIPC)' },
  { key: 'authorised_signatory', label: 'Authorised signatory resolution' },
  { key: 'trust_deed', label: 'Trust deed' },
  { key: 'source_of_funds', label: 'Source of funds' },
  { key: 'other', label: 'Other' },
];

export default function EditInvestorDialog({ open, onOpenChange, investor, investorTypes = [], onSaved }) {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tab, setTab] = useState('basic');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [kycDocs, setKycDocs] = useState([]);
  const [kycUploading, setKycUploading] = useState(false);
  const [kycDocType, setKycDocType] = useState('id_doc');
  const [audit, setAudit] = useState([]);
  // Live email — updated in place when admin uses ChangeEmailDialog so the
  // header + dialog description reflect the new address without a reload.
  const [liveEmail, setLiveEmail] = useState(investor?.email || '');
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [saRepoRate, setSaRepoRate] = useState(null);

  const loadFull = useCallback(async () => {
    if (!investor) return;
    try {
      const r = await axios.get(`${API_URL}/api/admin/users/${investor.id}/full-profile`, { headers });
      const u = r.data.user || {};
      const p = r.data.profile || {};
      setForm({
        first_name: u.first_name || '', last_name: u.last_name || '', phone: u.phone || '',
        risk_participant_code: u.risk_participant_code || '',
        investor_type: u.investor_type || u.brand || '',
        entity_name: p.entity_name || '', entity_type: p.entity_type || '', mandate_type: p.mandate_type || '',
        id_number: p.id_number || '', registration_number: p.registration_number || '',
        tax_number: p.tax_number || '', vat_number: p.vat_number || '',
        physical_address: p.physical_address || '', postal_address: p.postal_address || '',
        bank_name: p.bank_name || '', bank_branch_code: p.bank_branch_code || '',
        bank_account_number: p.bank_account_number || '', bank_account_type: p.bank_account_type || '',
        swift_code: p.swift_code || '',
        tax_residency: p.tax_residency || '', tax_reference: p.tax_reference || '',
        tax_status: p.tax_status || '', contact_person: p.contact_person || '',
        annual_management_fee_pct: p.annual_management_fee_pct ?? '',
        performance_sharing_rate_pct: p.performance_sharing_rate_pct ?? '',
        performance_hurdle_type: p.performance_hurdle_type || 'sa_repo_rate',
        performance_hurdle_fixed_pct: p.performance_hurdle_fixed_pct ?? '',
        notes: p.notes || '',
      });
    } catch (err) { toast.error('Failed to load profile'); }
    try {
      const rr = await axios.get(`${API_URL}/api/admin/investor-rate-config`, { headers });
      setSaRepoRate(rr.data.sa_repo_rate);
    } catch { /* non-fatal */ }
    try {
      const kr = await axios.get(`${API_URL}/api/admin/users/${investor.id}/kyc-documents`, { headers });
      setKycDocs(kr.data.documents || []);
    } catch { /* non-fatal */ }
    try {
      const ar = await axios.get(`${API_URL}/api/admin/users/${investor.id}/audit-log`, { headers });
      setAudit(ar.data.entries || []);
    } catch { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investor?.id, token]);

  useEffect(() => { if (open) { setTab('basic'); setLiveEmail(investor?.email || ''); loadFull(); } }, [open, loadFull, investor]);

  const NUMERIC_FIELDS = ['annual_management_fee_pct', 'performance_sharing_rate_pct', 'performance_hurdle_fixed_pct'];

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([_, v]) => v !== '' && v !== null && v !== undefined));
      NUMERIC_FIELDS.forEach(f => { if (body[f] !== undefined) body[f] = parseFloat(body[f]); });
      await axios.put(`${API_URL}/api/admin/users/${investor.id}/profile`, body, { headers });
      toast.success('Investor updated');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const setPassword = async () => {
    if (!newPw || newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!window.confirm(`Force-set password for ${investor.email}?\n\nThe investor will NOT be emailed. Share the new password securely.`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/users/${investor.id}/set-password`, { new_password: newPw }, { headers });
      toast.success('Password set');
      setNewPw(''); setPwOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const uploadKyc = async (file) => {
    if (!file) return;
    setKycUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(
        `${API_URL}/api/admin/users/${investor.id}/kyc-documents?document_type=${encodeURIComponent(kycDocType)}`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('KYC uploaded');
      loadFull();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'KYC upload failed');
    } finally { setKycUploading(false); }
  };

  const deleteKyc = async (docId) => {
    if (!window.confirm('Delete this KYC document? Action is logged in the audit trail.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/kyc-documents/${docId}`, { headers });
      toast.success('Deleted');
      loadFull();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  if (!investor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="edit-investor-dialog">
        <DialogHeader>
          <DialogTitle>
            Edit investor — {investor.first_name} {investor.last_name}
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono text-xs mr-2" data-testid="edit-investor-email">{liveEmail}</span>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[11px] px-2 py-0"
              onClick={() => setChangeEmailOpen(true)}
              data-testid="open-change-email-btn"
            >
              <Mail className="h-3 w-3 mr-1" /> Change email
            </Button>
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-6">
            <TabsTrigger value="basic" data-testid="edit-tab-basic">Basic</TabsTrigger>
            <TabsTrigger value="address" data-testid="edit-tab-address">Address</TabsTrigger>
            <TabsTrigger value="banking" data-testid="edit-tab-banking">Banking</TabsTrigger>
            <TabsTrigger value="tax" data-testid="edit-tab-tax">Tax &amp; KYC</TabsTrigger>
            <TabsTrigger value="fees" data-testid="edit-tab-fees">Fees &amp; Performance</TabsTrigger>
            <TabsTrigger value="account" data-testid="edit-tab-account">Account</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="grid grid-cols-1 md:grid-cols-2 gap-3 py-3">
            <div><Label>First name</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="edit-first-name" /></div>
            <div><Label>Last name</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
            <div><Label>Risk-Participant code</Label><Input value={form.risk_participant_code} onChange={e => setForm({...form, risk_participant_code: e.target.value})} /></div>
            <div className="md:col-span-2">
              <Label>Investor type <span className="text-xs text-gray-500 ml-1">(from Settings)</span></Label>
              <Select value={form.investor_type || '__none__'} onValueChange={v => setForm({...form, investor_type: v === '__none__' ? '' : v})}>
                <SelectTrigger data-testid="edit-investor-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {investorTypes.map(t => <SelectItem key={t.id} value={t.code}>{t.code} · {t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Entity name</Label><Input value={form.entity_name} onChange={e => setForm({...form, entity_name: e.target.value})} placeholder="Trust / CC name — leave blank for individuals" /></div>
            <div>
              <Label>Entity type</Label>
              <Select value={form.entity_type || '__none__'} onValueChange={v => setForm({...form, entity_type: v === '__none__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  <SelectItem value="Individual">Individual</SelectItem>
                  <SelectItem value="Trust">Trust</SelectItem>
                  <SelectItem value="Company">Company (Pty) Ltd</SelectItem>
                  <SelectItem value="CC">Close Corp (CC)</SelectItem>
                  <SelectItem value="Partnership">Partnership</SelectItem>
                  <SelectItem value="Retirement Fund">Retirement Fund</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mandate type <span className="text-xs text-gray-500 ml-1">(GIPS grouping)</span></Label>
              <Select value={form.mandate_type || '__none__'} onValueChange={v => setForm({...form, mandate_type: v === '__none__' ? '' : v})}>
                <SelectTrigger data-testid="edit-mandate-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— unset —</SelectItem>
                  <SelectItem value="discretionary">Discretionary</SelectItem>
                  <SelectItem value="non_discretionary">Non-discretionary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Notes (admin only)</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Internal notes — not visible to the investor" /></div>
          </TabsContent>

          <TabsContent value="address" className="grid grid-cols-1 gap-3 py-3">
            <div><Label>Physical address</Label><Textarea rows={3} value={form.physical_address} onChange={e => setForm({...form, physical_address: e.target.value})} /></div>
            <div><Label>Postal address</Label><Textarea rows={3} value={form.postal_address} onChange={e => setForm({...form, postal_address: e.target.value})} placeholder="Leave blank if same as physical" /></div>
          </TabsContent>

          <TabsContent value="banking" className="grid grid-cols-1 md:grid-cols-2 gap-3 py-3">
            <div><Label>Bank</Label><Input value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} placeholder="e.g. Standard Bank" /></div>
            <div><Label>Branch code</Label><Input value={form.bank_branch_code} onChange={e => setForm({...form, bank_branch_code: e.target.value})} /></div>
            <div><Label>Account number</Label><Input value={form.bank_account_number} onChange={e => setForm({...form, bank_account_number: e.target.value})} /></div>
            <div>
              <Label>Account type</Label>
              <Select value={form.bank_account_type || '__none__'} onValueChange={v => setForm({...form, bank_account_type: v === '__none__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  <SelectItem value="Cheque">Cheque / Current</SelectItem>
                  <SelectItem value="Savings">Savings</SelectItem>
                  <SelectItem value="Transmission">Transmission</SelectItem>
                  <SelectItem value="Business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>SWIFT / BIC <span className="text-xs text-gray-500 ml-1">(only for international payouts)</span></Label><Input value={form.swift_code} onChange={e => setForm({...form, swift_code: e.target.value.toUpperCase()})} /></div>
          </TabsContent>

          <TabsContent value="tax" className="space-y-4 py-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>ID number</Label><Input value={form.id_number} onChange={e => setForm({...form, id_number: e.target.value})} /></div>
              <div><Label>Registration number</Label><Input value={form.registration_number} onChange={e => setForm({...form, registration_number: e.target.value})} placeholder="For entities" /></div>
              <div><Label>Tax number</Label><Input value={form.tax_number} onChange={e => setForm({...form, tax_number: e.target.value})} /></div>
              <div><Label>VAT number</Label><Input value={form.vat_number} onChange={e => setForm({...form, vat_number: e.target.value})} /></div>
              <div><Label>Tax residency</Label><Input value={form.tax_residency} onChange={e => setForm({...form, tax_residency: e.target.value})} placeholder="South Africa" /></div>
              <div><Label>Tax reference</Label><Input value={form.tax_reference} onChange={e => setForm({...form, tax_reference: e.target.value})} placeholder="SARS reference" /></div>
              <div>
                <Label>Tax status</Label>
                <Select value={form.tax_status || '__none__'} onValueChange={v => setForm({...form, tax_status: v === '__none__' ? '' : v})}>
                  <SelectTrigger data-testid="edit-tax-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— none —</SelectItem>
                    <SelectItem value="sa_resident">SA resident</SelectItem>
                    <SelectItem value="non_resident">Non-resident</SelectItem>
                    <SelectItem value="exempt">Exempt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Contact person (auth. signatory)</Label><Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} /></div>
            </div>

            {/* KYC documents */}
            <div className="border-t pt-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h4 className="font-semibold text-[#1D2D2A] flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" /> KYC documents ({kycDocs.filter(d => d.is_current).length} current)
                </h4>
                <div className="flex items-center gap-2">
                  <Select value={kycDocType} onValueChange={setKycDocType}>
                    <SelectTrigger className="w-56" data-testid="kyc-doc-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {KYC_DOC_TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <label className={`inline-flex items-center gap-1 px-3 py-2 rounded-md border cursor-pointer text-sm ${kycUploading ? 'opacity-50 pointer-events-none' : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'}`} data-testid="kyc-upload-btn">
                    <Upload className="h-4 w-4" />
                    {kycUploading ? 'Uploading…' : 'Upload'}
                    <input type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) uploadKyc(f); e.target.value=''; }} />
                  </label>
                </div>
              </div>
              {kycDocs.length === 0 ? (
                <div className="text-xs text-[#6C757D] italic p-4 bg-slate-50 rounded">No KYC docs on file yet.</div>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {kycDocs.map(d => (
                    <div key={d.id} className={`flex items-center justify-between bg-white border rounded px-3 py-2 text-sm ${!d.is_current ? 'opacity-60' : ''}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-emerald-700 shrink-0" />
                        <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline truncate">{d.file_name}</a>
                        <Badge variant="outline" className="text-[10px]">{d.document_type}</Badge>
                        {!d.is_current && <Badge variant="outline" className="text-[10px]">v{d.version} (superseded)</Badge>}
                        {d.status && <Badge variant="outline" className="text-[10px]">{d.status}</Badge>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs text-[#6C757D]">{new Date(d.uploaded_at).toLocaleDateString()}</span>
                        <button onClick={() => deleteKyc(d.id)} className="text-red-600 hover:text-red-700" data-testid={`kyc-delete-${d.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="fees" className="grid grid-cols-1 md:grid-cols-2 gap-3 py-3">
            <div>
              <Label>Annual management fee (%)</Label>
              <Input type="number" step="0.01" value={form.annual_management_fee_pct}
                     onChange={e => setForm({...form, annual_management_fee_pct: e.target.value})}
                     placeholder="e.g. 1.5" data-testid="edit-mgmt-fee" />
            </div>
            <div>
              <Label>Performance sharing rate (%)</Label>
              <Input type="number" step="0.01" value={form.performance_sharing_rate_pct}
                     onChange={e => setForm({...form, performance_sharing_rate_pct: e.target.value})}
                     placeholder="e.g. 20" data-testid="edit-perf-share" />
            </div>
            <div>
              <Label>Performance hurdle</Label>
              <Select value={form.performance_hurdle_type || 'sa_repo_rate'} onValueChange={v => setForm({...form, performance_hurdle_type: v})}>
                <SelectTrigger data-testid="edit-hurdle-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sa_repo_rate">SA Repo Rate {saRepoRate != null ? `(currently ${(saRepoRate * 100).toFixed(2)}%)` : ''}</SelectItem>
                  <SelectItem value="fixed">Fixed rate</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">SA Repo Rate is set once for all investors under Settings → Investor Rates.</p>
            </div>
            {form.performance_hurdle_type === 'fixed' && (
              <div>
                <Label>Fixed hurdle rate (%)</Label>
                <Input type="number" step="0.01" value={form.performance_hurdle_fixed_pct}
                       onChange={e => setForm({...form, performance_hurdle_fixed_pct: e.target.value})}
                       placeholder="e.g. 8" data-testid="edit-hurdle-fixed" />
              </div>
            )}
          </TabsContent>

          <TabsContent value="account" className="space-y-4 py-3">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
              <div className="flex gap-2 items-start"><KeyRound className="h-4 w-4 mt-0.5" />
                <div className="flex-1">
                  <strong>Force-set password</strong> — use only when the investor can&apos;t receive email.
                  {pwOpen ? (
                    <div className="mt-2 flex items-center gap-2">
                      <Input type="text" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="At least 8 characters" data-testid="admin-set-pw-input" />
                      <Button size="sm" onClick={setPassword} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-set-pw-confirm">Set</Button>
                      <Button size="sm" variant="outline" onClick={() => { setPwOpen(false); setNewPw(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="mt-2" onClick={() => setPwOpen(true)} data-testid="admin-set-pw-open">Set password directly</Button>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t pt-3" data-testid="audit-log">
              <h4 className="font-semibold text-[#1D2D2A] flex items-center gap-2 mb-2"><Info className="h-4 w-4" /> Audit trail ({audit.length})</h4>
              {audit.length === 0 ? (
                <div className="text-xs text-[#6C757D] italic p-4 bg-slate-50 rounded">No admin actions recorded yet.</div>
              ) : (
                <div className="space-y-1 max-h-56 overflow-y-auto text-xs">
                  {audit.map(a => (
                    <div key={a.id} className="flex justify-between bg-white border rounded px-3 py-1.5">
                      <div>
                        <span className="font-mono text-[10px] text-slate-500">{a.event}</span> — <span className="text-slate-800">{a.actor_email}</span>
                        {a.document_type && <span className="text-slate-500"> · {a.document_type}</span>}
                      </div>
                      <span className="text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Close</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="edit-investor-save">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <ChangeEmailDialog
        open={changeEmailOpen}
        onOpenChange={setChangeEmailOpen}
        user={{ id: investor?.id, email: liveEmail, first_name: investor?.first_name, last_name: investor?.last_name }}
        onSaved={(res) => {
          setLiveEmail(res.new_email);
          onSaved?.();  // refresh the parent list so the row shows the new address
        }}
      />
    </Dialog>
  );
}
